import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, BadgeCheck, BedDouble, Bath, Ruler, Car, MapPin, Building } from 'lucide-react';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { fetchMerchantItem } from '../hooks/useMerchantMarket';
import PriceDisplay from '../components/marketplace/PriceDisplay';
import ContactActions from '../components/marketplace/ContactActions';
import StoreUidBadge from '../components/marketplace/StoreUidBadge';
import SafetyTips from '../components/marketplace/SafetyTips';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=60';

export default function PropertyDetailsPage() {
  const { id } = useParams();
  const location = useLocation();
  const cachedStore = location.state?.store || null;
  const cachedItem = location.state?.item || null;
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const [data, setData] = useState({ item: cachedItem, store: cachedStore });
  const [loading, setLoading] = useState(!cachedItem);
  const [activeImg, setActiveImg] = useState(0);
  const BackIcon = isAr ? ArrowRight : ArrowLeft;

  useEffect(() => {
    let mounted = true;
    fetchMerchantItem('real_estate', id, cachedStore)
      .then(res => { if (mounted) setData((prev) => ({ item: res.item || prev.item, store: res.store || prev.store })); })
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" /></div>;
  if (!data.item) return <div className="min-h-screen flex items-center justify-center text-red-500">Not found</div>;
  const { item, store } = data;
  const images = (item.images && item.images.length) ? item.images : [item.image || PLACEHOLDER];
  const isRent = item.listingType === 'for_rent';
  const title = item.title || `${item.propertyType} - ${item.emirate}`;

  const spec = (label, value, Icon) => value == null || value === '' ? null : (
    <div className={`rounded-xl p-3 flex items-center gap-2 ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
      {Icon && <Icon className="h-4 w-4 text-[#1E9BD9]" />}
      <div className="text-xs">
        <div className="text-slate-500">{label}</div>
        <div className="font-semibold text-[#0F172A] dark:text-white">{value}</div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/properties" className="inline-flex items-center gap-1.5 text-sm text-[#1E9BD9] hover:underline mb-6" data-testid="back-link">
          <BackIcon className="h-4 w-4" />
          {isAr ? 'العودة إلى العقارات' : 'Back to Properties'}
        </Link>

        <div className={`rounded-3xl overflow-hidden ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border border-[#E2E8F0] shadow-lg'}`}>
          <div className="aspect-[16/9] bg-slate-100 dark:bg-white/5 overflow-hidden relative">
            <img src={images[activeImg]} alt={title} className="w-full h-full object-cover" />
            <span className="absolute top-4 start-4 px-3 py-1 rounded-full text-xs font-semibold bg-white/95 text-[#1E9BD9] backdrop-blur">
              {isRent ? (isAr ? 'للإيجار' : 'For Rent') : (isAr ? 'للبيع' : 'For Sale')}
            </span>
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 p-3 overflow-x-auto">
              {images.map((src, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${i === activeImg ? 'border-[#1E9BD9]' : 'border-transparent'}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="p-6 md:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] dark:text-white mb-2">{title}</h1>
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {[item.building_name, item.area_name, item.emirate].filter(Boolean).join(', ')}
                </p>
              </div>
              <PriceDisplay item={item} locale={isAr ? 'ar' : 'en'} size="lg" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {spec(isAr ? 'غرف النوم' : 'Bedrooms', item.bedrooms, BedDouble)}
              {spec(isAr ? 'الحمامات' : 'Bathrooms', item.bathrooms, Bath)}
              {spec(isAr ? 'المساحة' : 'Area', item.area != null ? `${Number(item.area).toLocaleString()} ft²` : null, Ruler)}
              {spec(isAr ? 'مواقف' : 'Parking', item.parking, Car)}
              {spec(isAr ? 'الطابق' : 'Floor', item.floor, Building)}
              {isRent && spec(isAr ? 'المفروشات' : 'Furnishing', item.furnishing)}
              {isRent && spec(isAr ? 'الشيكات' : 'Cheques', item.cheques)}
              {!isRent && spec(isAr ? 'الملكية' : 'Ownership', item.ownership)}
              {!isRent && spec(isAr ? 'الإنجاز' : 'Completion', item.completion)}
              {!isRent && spec(isAr ? 'سنة البناء' : 'Year Built', item.year_built)}
            </div>

            {Array.isArray(item.amenities) && item.amenities.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold mb-2 text-[#0F172A] dark:text-white">
                  {isAr ? 'المرافق' : 'Amenities'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {item.amenities.map((a, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-full text-xs bg-[#1E9BD9]/10 text-[#1E9BD9]">{a}</span>
                  ))}
                </div>
              </div>
            )}

            {item.description && (
              <div className={`rounded-xl p-4 mb-6 text-sm leading-relaxed ${isDarkMode ? 'bg-white/5 text-white/80' : 'bg-slate-50 text-slate-700'}`}>
                {item.description}
              </div>
            )}

            {store?.id && (
              <div className={`rounded-xl p-4 mb-4 flex items-center gap-3 ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                <Link to={`/store/${store.id}`} className="flex items-center gap-3 min-w-0" data-testid="store-strip">
                  {store.logo && <img src={store.logo} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-[#0F172A] dark:text-white truncate">
                        {isAr ? (store.name || store.nameEn) : (store.nameEn || store.name)}
                      </span>
                      {store.verified && <BadgeCheck className="h-4 w-4 text-[#1E9BD9] shrink-0" />}
                    </div>
                    <StoreUidBadge store={store} locale={isAr ? 'ar' : 'en'} className="mt-1" />
                  </div>
                </Link>
              </div>
            )}

            <div className="mb-6">
              <SafetyTips category="real_estate" isAr={isAr} isDarkMode={isDarkMode} />
            </div>

            <ContactActions store={store} itemTitle={title} locale={isAr ? 'ar' : 'en'} />
          </div>
        </div>
      </div>
    </div>
  );
}
